// Registry of workflow-file preview plugins. See README.md for the shape.
// Ordering matters: the first plugin whose `matches` returns truthy wins.
// More-specific plugins should come first so they don't get swallowed
// by a more generic one.
import imagePlugin from './image'
import markdownPlugin from './markdown'
import { normalizeWorkflowFile } from './normalizeFile'

export { normalizeWorkflowFile }
export const workflowPreviewPlugins = [imagePlugin, markdownPlugin]

function runMatches(plugin, ctx) {
  try {
    return plugin.matches?.(ctx) ?? false
  } catch (err) {
    // A broken plugin must never take down the raw fallback.
    console.warn(`workflow-preview plugin "${plugin.id}" matches() threw:`, err)
    return false
  }
}

/**
 * Pre-fetch hook. Runs each plugin's `matches()` with `content: null`
 * so metadata-decidable plugins can tell the host how to fetch the
 * peek (e.g. "give me the full file, not the default 10k snippet").
 * Returns `{ plugin, params, responseType }` where `params` is whatever
 * `plugin.peekParams({ file })` produced and `responseType` is the
 * plugin's `peekResponseType` ('blob' for binary formats, undefined for
 * the default text/JSON fetch) — an empty object and no plugin if none
 * matches on metadata alone.
 */
export function computePeekParams(file) {
  const normalized = normalizeWorkflowFile(file)
  const ctx = { file: normalized, content: null }
  for (const plugin of workflowPreviewPlugins) {
    if (!runMatches(plugin, ctx)) continue
    let params = {}
    try {
      params = plugin.peekParams?.({ file: normalized }) ?? {}
    } catch (err) {
      console.warn(
        `workflow-preview plugin "${plugin.id}" peekParams() threw:`,
        err,
      )
    }
    return { plugin, params, responseType: plugin.peekResponseType }
  }
  return { plugin: null, params: {}, responseType: undefined }
}

/**
 * Final pick. Called after the peek has been fetched so plugins that
 * need the actual bytes to decide (or confirm) get the real content.
 */
export function pickPlugin({ file, content }) {
  const normalized = normalizeWorkflowFile(file)
  const ctx = { file: normalized, content }
  for (const plugin of workflowPreviewPlugins) {
    const parsed = runMatches(plugin, ctx)
    if (parsed) return { plugin, parsed, file: normalized }
  }
  return null
}
